/**
 * @file RepOrderRequestCard.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-30
 * @modified 2026-10-30
 * @description One rep order request as a card (Cam, 29 Oct 2026) — the rep's
 *              "Your requests" on their dashboard and the office's "Rep
 *              requests" window alike: what is asked for (each product with
 *              its quantity and unit, the delivery or the schedule, notes),
 *              for a change what it was → what is asked, who sent it and where
 *              it stands in words (utils/repOrderRequests statusOf). Actions
 *              are the caller's (`actions`).
 */

import React from 'react';
import {
  KIND_WORDS, statusOf, lineText, deliveryText, scheduleText, amendChanges, requestRef, whenText
} from '../utils/repOrderRequests';
import './RepOrderRequests.css';

function RepOrderRequestCard({ request, viewer = 'rep', actions = null, onOpenOrder }) {
  const r = request || {};
  const status = statusOf(r);
  const amend = r.kind === 'amend_order' || r.kind === 'amend_standing_order';
  const changes = amend ? amendChanges(r) : [];
  const when = whenText(r.created_at);
  const what = r.kind === 'amend_order'
    ? (r.order_number || r.snapshot?.order_number || 'an order')
    : (r.kind === 'amend_standing_order' ? (r.snapshot?.reference || `Standing Order #${r.standing_order_id}`) : null);
  const scheduleOrDelivery = r.schedule ? scheduleText(r.schedule) : deliveryText(r.delivery);

  return (
    <div className={`ror-card status-${status.key}`}>
      <div className="ror-card-head">
        <span className="ror-kind">{KIND_WORDS[r.kind] || r.kind}</span>
        <span className={`ror-status tone-${status.tone}`}>{status.text}</span>
      </div>
      <div className="ror-card-who">
        {r.account_no && <span className="account-badge-inline">{r.account_no}</span>}
        <span className="ror-card-customer">{r.account_name || 'Customer'}</span>
      </div>
      {what && (
        <div className="ror-card-target">
          Change to{' '}
          {r.kind === 'amend_order' && onOpenOrder && r.order_id ? (
            <button type="button" className="ror-link" data-read-safe onClick={() => onOpenOrder(r.order_id)}>{what}</button>
          ) : what}
        </div>
      )}

      <ul className="ror-card-lines">
        {(r.lines || []).map((l, i) => (
          <li key={i}>
            {lineText(l)}
            {l.item_is_archived && <span className="ror-mini-chip tone-error">Archived</span>}
            {l.note && <span className="ror-card-note"> — {l.note}</span>}
          </li>
        ))}
      </ul>
      {scheduleOrDelivery && (
        <div className="ror-card-when">
          <span className="material-symbols-rounded" aria-hidden="true">{r.schedule ? 'event_repeat' : 'local_shipping'}</span>
          {scheduleOrDelivery}
        </div>
      )}
      {amend && changes.length > 0 && (
        <div className="ror-card-changes">
          <span className="ror-label">What changes</span>
          <ul>{changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </div>
      )}
      {amend && changes.length === 0 && (
        <div className="ror-card-changes"><span className="ror-hint">Nothing differs from {what} — see the notes.</span></div>
      )}
      {r.notes && <div className="ror-card-notes">“{r.notes}”</div>}

      <div className="ror-card-meta">
        {requestRef(r.id)}
        {viewer === 'office' && r.rep_name ? ` · ${r.rep_name}` : ''}
        {viewer === 'office' && r.requested_by_name && r.requested_by_name !== r.rep_name ? ` (sent by ${r.requested_by_name})` : ''}
        {when ? ` · sent ${when}` : ''}
        {r.status === 'completed' && r.completed_by_name ? ` · done by ${r.completed_by_name}` : ''}
        {r.status === 'closed' && r.closed_by_name ? ` · closed by ${r.closed_by_name}` : ''}
      </div>
      {actions && <div className="ror-card-actions">{actions}</div>}
    </div>
  );
}

export default RepOrderRequestCard;
